import { store } from '../../../store';
import {
  loadGlobalTokens,
  saveGlobalTokens,
  type GlobalTokens,
} from '../../../utils/tenantSessionStorage';
import { safeParseJwtPayload } from '../../../utils/jwt';
import { applyTenantRegion } from './switchActiveOrganization';
import { persistTenantSessions, selectTenantSessionById, updateTenantSessionTokens } from '.';

/** Returns the stored tokens for a tenant, or null if not cached. */
export function loadPerTenantTokens(tenantId: number | string): GlobalTokens | null {
  const session = selectTenantSessionById(tenantId)(store.getState());
  if (!session) return null;
  const { accessToken, refreshToken, sessionId } = session;
  if (!accessToken || !refreshToken || !sessionId) return null;
  return { accessToken, refreshToken, sessionId };
}

export function activateSessionByTenantId(tenantId: number | string) {
  const session = selectTenantSessionById(tenantId)(store.getState());
  if (!session) {
    return false;
  }

  const { accessToken, refreshToken, sessionId, region } = session;
  saveGlobalTokens({ accessToken, refreshToken, sessionId });
  applyTenantRegion(region);
  return true;
}

/**
 * Copies the current global tokens into a tenant's session. Called before
 * starting the add-organisation login flow so the existing session isn't lost
 * when the exchange overwrites the global slots with the new org's tokens.
 */
export function snapshotGlobalTokensToTenant(tenantId: number): boolean {
  const tokens = loadGlobalTokens();
  if (!tokens) return false;
  store.dispatch(updateTenantSessionTokens({ tenantId, tokens }));
  persistTenantSessions(store.getState());
  return true;
}

/** Updates the active tenant's tokens after refresh token rotation. */
export function updateActiveTenantSessionTokens(
  activeTenantId: number | null,
  tokens: GlobalTokens,
  expectedUserId?: number | null
): boolean {
  if (activeTenantId == null) return false;

  if (expectedUserId != null) {
    const tokenUserId = Number(safeParseJwtPayload(tokens.accessToken)?.sub ?? NaN);
    if (Number.isFinite(tokenUserId) && tokenUserId > 0 && tokenUserId !== expectedUserId) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[updateActiveTenantSessionTokens] token sub=${tokenUserId} mismatches active tenant ${activeTenantId} (expected user ${expectedUserId}); skipping.`
        );
      }
      return false;
    }
  }

  store.dispatch(updateTenantSessionTokens({ tenantId: activeTenantId, tokens }));
  persistTenantSessions(store.getState());
  return true;
}
